import { z } from 'zod';
import { PasswordSchema } from './auth.js';
import { ChatMessageSchema, InterruptPromptSchema, InterruptResolveRequestSchema } from './chat.js';
import { DashboardDirectionSchema, DashboardIntentSchema } from './dashboard.js';
import { FileUploadKindSchema, FileUploadStatusSchema } from './files.js';
import { StorageConfigInputSchema, StorageTypeSchema } from './onboarding.js';
import { ToolVisibilitySchema } from './skills.js';
import { UserAttributesRecordSchema } from './user-attributes.js';
export const SystemInfoStorageSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('s3'),
        endpoint: z.string(),
        bucket: z.string(),
        region: z.string(),
        forcePathStyle: z.boolean(),
    }),
    z.object({
        type: z.enum(['fs', 'nfs']),
        rootPath: z.string(),
    }),
]);
export const SystemInfoSchema = z.object({
    db: z.object({
        host: z.string(),
        port: z.number().int(),
        database: z.string(),
        username: z.string(),
    }),
    /** null 이면 스토리지 미구성 — 파일 업로드 기능이 비활성화됨. */
    storage: SystemInfoStorageSchema.nullable(),
    /**
     * 전역 기본 LLM. endpoint 또는 bonding 둘 중 하나가 설정될 수 있고, 둘 다 미설정이면 null.
     * 본딩(랜덤 분산)은 멤버에 따라 baseUrl/model 이 매번 달라지므로 표면화하지 않고 멤버 수만 노출.
     */
    llmDefault: z
        .discriminatedUnion('kind', [
        z.object({
            kind: z.literal('endpoint'),
            id: z.string().uuid(),
            name: z.string(),
            displayName: z.string().nullable(),
            baseUrl: z.string(),
            model: z.string(),
        }),
        z.object({
            kind: z.literal('bonding'),
            id: z.string().uuid(),
            name: z.string(),
            displayName: z.string().nullable(),
            memberCount: z.number().int().nonnegative(),
        }),
    ])
        .nullable(),
});
export const HealthStatusSchema = z.enum(['ok', 'error', 'skipped']);
export const HealthCheckSchema = z.object({
    status: HealthStatusSchema,
    latencyMs: z.number().int().nullable(),
    message: z.string().nullable(),
});
export const SystemHealthSchema = z.object({
    db: HealthCheckSchema,
    /** storage 가 미구성이면 status='skipped'. */
    storage: HealthCheckSchema,
    llm: HealthCheckSchema,
    checkedAt: z.string(),
});
export const RoleSchema = z.enum(['admin', 'user']);
export const AdminUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    role: RoleSchema,
    toolVisibilityOverride: ToolVisibilitySchema.nullable(),
    disabledAt: z.string().nullable(),
    createdAt: z.string(),
    /** 이 사용자에게 허용된 Skill id 목록. admin 은 [] 여도 전체 권한. */
    allowedSkillIds: z.array(z.string().uuid()),
    /** 이 사용자가 추가 관리자 권한을 가진 데이터소스 id 목록. admin 은 [] 여도 전체 권한. */
    dataSourceAdminIds: z.array(z.string().uuid()),
    /** 데이터소스별 외부 정체성 (예: { "<dataSourceId>": "hong@co.kr" }). user_scope_column 필터에 사용. */
    externalIdentities: z.record(z.string()),
    /** LLM API 릴레이 키 발급 권한. 글로벌 토글이 ON 일 때만 효력. */
    apiRelayEnabled: z.boolean(),
    /** 동적 사용자 항목 값 (user_attribute_definitions 의 key → 값). soft-deleted 정의 값은 포함하지 않음. */
    attributes: UserAttributesRecordSchema,
});
export const AdminUserListSchema = z.object({
    users: z.array(AdminUserSchema),
    /** 필터(search) 조건을 만족하는 전체 건수 — 무한 스크롤 종료 판정용. */
    total: z.number().int().nonnegative(),
});
export const AdminUserListQuerySchema = z.object({
    /** 이름(displayName) 또는 이메일 부분일치, 대소문자 무시. trim 후 빈 문자열은 무시. */
    search: z.string().trim().min(1).max(100).optional(),
    /** 페이지 크기. default 200, max 200 (서버 측 가드). 드롭다운 무한 스크롤에서 사용. */
    limit: z.coerce.number().int().min(1).max(200).optional(),
    /** 페이지 오프셋. default 0. limit 와 함께 사용. */
    offset: z.coerce.number().int().nonnegative().optional(),
});
export const CreateUserRequestSchema = z.object({
    /**
     * 로그인 식별자. 이메일 형식 또는 사번/SSO 아이디 (예: "E1000", "hong.gildong").
     * 회사 SSO/LDAP 호환을 위해 형식 검증은 길이만 — 어드민이 책임지고 입력.
     */
    email: z.string().min(1).max(254),
    displayName: z.string().min(1).max(80),
    role: RoleSchema,
    password: PasswordSchema,
    /** 신규 사용자에게 바로 허용할 Skill id 목록 (선택). 관리자는 무시된다. */
    allowedSkillIds: z.array(z.string().uuid()).optional(),
    /** 추가 데이터소스 관리자 권한 (선택). 관리자는 무시된다. */
    dataSourceAdminIds: z.array(z.string().uuid()).optional(),
    /** 데이터소스별 외부 정체성 매핑 (선택). */
    externalIdentities: z.record(z.string()).optional(),
    /** LLM API 릴레이 키 발급 권한 부여 여부 (기본 false). */
    apiRelayEnabled: z.boolean().optional(),
    /** 동적 사용자 항목 값 (선택). 정의되지 않은 key 는 거부. required 인 항목 누락 시 거부. */
    attributes: UserAttributesRecordSchema.optional(),
});
export const UpdateUserRequestSchema = z.object({
    displayName: z.string().min(1).max(80).optional(),
    role: RoleSchema.optional(),
    disabled: z.boolean().optional(),
    toolVisibilityOverride: ToolVisibilitySchema.nullable().optional(),
    /** 지정하면 기존 grant 를 모두 제거하고 이 배열로 교체. 관리자엔 무시. */
    allowedSkillIds: z.array(z.string().uuid()).optional(),
    /** 지정하면 기존 data source admin grant 를 이 배열로 교체. 관리자엔 무시. */
    dataSourceAdminIds: z.array(z.string().uuid()).optional(),
    /** 지정하면 external_identities 전체 교체. key=dataSourceId, value=identity value. */
    externalIdentities: z.record(z.string()).optional(),
    /** LLM API 릴레이 키 발급 권한 토글. */
    apiRelayEnabled: z.boolean().optional(),
    /** 지정하면 user_attribute_values 전체 교체 (정의된 key 만). 미지정이면 기존 값 유지. */
    attributes: UserAttributesRecordSchema.optional(),
});
export const ResetPasswordResponseSchema = z.object({
    temporaryPassword: z.string(),
});
export const AuditLogSchema = z.object({
    id: z.string().uuid(),
    actorUserId: z.string().uuid().nullable(),
    actorEmail: z.string().nullable(),
    actorDisplayName: z.string().nullable(),
    action: z.string(),
    target: z.string().nullable(),
    meta: z.record(z.unknown()),
    createdAt: z.string(),
});
export const AuditLogListSchema = z.object({
    logs: z.array(AuditLogSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int(),
});
/**
 * 관리자가 조정 가능한 전역 시스템 설정.
 * 현재는 채팅 툴 호출 한도만 노출. 향후 추가 필드는 모두 optional 로.
 */
export const SystemSettingsSchema = z.object({
    /** 하나의 assistant turn 에서 허용할 툴 호출 iteration 수 상한. */
    toolIterationLimit: z.number().int().min(1).max(50),
    /** cross-domain (활성 skill 2개 이상) 세션 한정 상한. 단일 skill 보다 보통 크다. */
    toolIterationLimitMulti: z.number().int().min(1).max(50),
    /** 한 챗 turn 의 엑셀 lazy ingest 에서 동시에 처리할 (파일×시트) 작업 상한. */
    excelIngestConcurrency: z.number().int().min(1).max(20),
    /** LLM 보조 엑셀 구조 인식 활성화 (다중 표/병합 헤더 대시보드). 기본 OFF. */
    excelLlmStructure: z.boolean(),
    /** 구조 인식 시 LLM 에 보여줄 시트 상단 행 수. */
    excelLlmTopN: z.number().int().min(10).max(300),
    /** 구조 인식 시 LLM 에 보여줄 최대 열 수 (자동 축소·토큰 상한, 실제 적재 열은 코드가 끝까지 스캔). */
    excelLlmMaxCol: z.number().int().min(4).max(120),
    /** 구조 인식 검증 실패 시 재시도 최대 횟수. */
    excelLlmRetries: z.number().int().min(1).max(5),
    /** 채팅 UI 에서 어시스턴트 답변 옆에 응답 소요 시간을 표시할지 여부. */
    showResponseTime: z.boolean(),
    /** 어시스턴트 페르소나 이름 (예: "김부장"). */
    personaName: z.string().min(1).max(40),
    /** 어시스턴트 페르소나 성격/말투 (예: "모든 일을 깔끔하게 처리"). */
    personaDescription: z.string().min(1).max(200),
    /** 사용자 사이드바 '지식 위키' 메뉴 노출 여부. */
    wikiEnabled: z.boolean(),
    /** 사용자 채팅 화면 모델 선택 버튼(ModelPicker) 노출 여부. false 면 버튼이 숨겨지고 기본 모델 사용. */
    userModelSelectionEnabled: z.boolean(),
    /** 사용자 자동 로그아웃까지의 분. 마지막 API 활동 이후 이 시간 동안 활동 없으면 다음 요청에서 401. 0 = 비활성화 (24h 절대 만료). */
    idleTimeoutMinutes: z.number().int().min(0).max(1440),
    /** dashboard.create 기본 비주얼 디렉션. LLM input / prev artifact 둘 다 없을 때 폴백. */
    dashboardDefaultDirection: DashboardDirectionSchema,
    /** dashboard.create 기본 설계 방향 (intent). 위와 같은 폴백 규칙. */
    dashboardDefaultIntent: DashboardIntentSchema,
    /** 정체성(모델 출처) 환각 가드 on/off. */
    identityGuardEnabled: z.boolean(),
    /** 출력 스캔 금지 패턴 (개행 구분). 빈 문자열이면 내장 기본값. */
    identityGuardPatterns: z.string(),
    /** 정체성 매치 시 치환할 고정 답변. 빈 문자열이면 persona/brand 기반 자동 생성. */
    identityCannedAnswer: z.string(),
    /** 금칙어 전역 차단 적용 토글. off 여도 탐지·플래그는 동작. */
    bannedWordsEnforce: z.boolean(),
    /** 금칙어 차단 시 고정 거부 문구. 빈 문자열이면 내장 기본값. */
    bannedWordsRefusalText: z.string(),
});
/**
 * fs/nfs 의 disk 사용량. fs.statfs 결과 — 단위는 바이트.
 */
export const StorageUsageSchema = z.object({
    total: z.number().int().nonnegative(),
    available: z.number().int().nonnegative(),
    free: z.number().int().nonnegative(),
    used: z.number().int().nonnegative(),
});
/**
 * 관리자 화면에서 노출하는 현재 스토리지 상태. secretKey/accessKey 평문은 절대 노출하지 않음.
 * 미구성 시에도 existingFileCount 는 알려준다 — 변경 경고 모달이 file_uploads 가 1개 이상일 때
 * 발동되기 때문. 일반 z.union 사용 (configured=true 가 두 멤버에 등장해 discriminatedUnion 불가).
 * TS 측 타입 narrowing 은 configured / type 모두 literal 이므로 자동.
 */
const AdminStorageUnconfiguredSchema = z.object({
    configured: z.literal(false),
    /** 이전에 한 번이라도 등록했던 type — UI 가 기본 탭 선택에 활용. 미설정 환경이면 null. */
    lastType: StorageTypeSchema.nullable(),
    existingFileCount: z.number().int().nonnegative(),
});
const AdminStorageS3Schema = z.object({
    configured: z.literal(true),
    type: z.literal('s3'),
    endpoint: z.string(),
    bucket: z.string(),
    region: z.string(),
    forcePathStyle: z.boolean(),
    /** 관리자 화면에서 마스킹 표시용 — accessKey 의 첫 4자 + ****. */
    accessKeyMasked: z.string(),
    updatedAt: z.string(),
    /** S3 는 quota 개념이 없어 항상 null. */
    usage: StorageUsageSchema.nullable(),
    existingFileCount: z.number().int().nonnegative(),
    /** DuckDB 등 로컬 파일이 필요한 사본 캐시 디렉터리. 미설정이면 OS 기본 tmpdir. */
    tempCachePath: z.string().nullable(),
});
const AdminStorageFsSchema = z.object({
    configured: z.literal(true),
    type: z.enum(['fs', 'nfs']),
    rootPath: z.string(),
    updatedAt: z.string(),
    /** statfs 실패 (마운트 stale / 권한 부족 등) 시 null. */
    usage: StorageUsageSchema.nullable(),
    existingFileCount: z.number().int().nonnegative(),
});
export const AdminStorageConfigSchema = z.union([
    AdminStorageUnconfiguredSchema,
    AdminStorageS3Schema,
    AdminStorageFsSchema,
]);
/**
 * 백엔드 변경 시 기존 파일에 접근 불가 — 사용자가 모달로 명시 확인했음을 표현하는 플래그.
 * 신규 설정 (configured=false) 이거나 식별자가 동일하면 무시된다.
 */
export const UpdateStorageRequestSchema = z.object({
    config: StorageConfigInputSchema,
    confirmReplaceExistingFiles: z.boolean().default(false),
});
/**
 * 저장 전 연결 테스트.
 * - s3: HeadBucket → 없으면 CreateBucket
 * - fs/nfs: rootPath access(R_OK | W_OK) + 임시 파일 write/read/unlink
 */
export const TestStorageRequestSchema = StorageConfigInputSchema;
export const TestStorageResponseSchema = z.object({
    ok: z.boolean(),
    /** 200 / 404 / 403 등. 네트워크 실패 / fs 검증은 null. */
    httpStatus: z.number().int().nullable(),
    /** s3: AWS SDK 가 해석한 에러 name. fs/nfs: 'EACCES', 'ENOENT', 'ENOTDIR', 'EROFS' 등. */
    errorCode: z.string().nullable(),
    message: z.string(),
    /** s3 에서 ok=false 지만 버킷이 없을 뿐이라면 createBucket 으로 해결 가능. fs/nfs 는 항상 false. */
    bucketMissing: z.boolean(),
});
/**
 * UpdateStorageRequest 응답.
 * - ok=true 면 즉시 반영 완료.
 * - ok=false, reason='replace_requires_confirm' 이면 프런트가 변경 경고 모달을 띄우고
 *   confirmReplaceExistingFiles=true 로 재호출해야 한다 (다중 탭 race / 직접 API 호출 보호용 안전망).
 */
export const UpdateStorageResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true) }),
    z.object({
        ok: z.literal(false),
        reason: z.literal('replace_requires_confirm'),
        existingFileCount: z.number().int().nonnegative(),
    }),
]);
// ── SMTP 설정 ────────────────────────────────────────────────────
export const AdminSmtpConfigSchema = z.discriminatedUnion('configured', [
    z.object({ configured: z.literal(false) }),
    z.object({
        configured: z.literal(true),
        host: z.string(),
        port: z.number().int(),
        secure: z.boolean(),
        username: z.string().nullable(),
        fromEmail: z.string(),
        fromName: z.string(),
        updatedAt: z.string(),
    }),
]);
export const UpdateSmtpConfigRequestSchema = z.object({
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535).default(587),
    secure: z.boolean().default(false),
    username: z.string().optional(),
    /** 빈 문자열이면 기존 비밀번호 유지. */
    password: z.string().optional(),
    fromEmail: z.string().email(),
    fromName: z.string().min(1).default('innermate'),
});
export const TestSmtpRequestSchema = z.object({
    to: z.string().email(),
});
// ─────────────────────────────────────────────────────────────────
export const UpdateSystemSettingsRequestSchema = z
    .object({
    toolIterationLimit: z.number().int().min(1).max(50).optional(),
    toolIterationLimitMulti: z.number().int().min(1).max(50).optional(),
    excelIngestConcurrency: z.number().int().min(1).max(20).optional(),
    excelLlmStructure: z.boolean().optional(),
    excelLlmTopN: z.number().int().min(10).max(300).optional(),
    excelLlmMaxCol: z.number().int().min(4).max(120).optional(),
    excelLlmRetries: z.number().int().min(1).max(5).optional(),
    showResponseTime: z.boolean().optional(),
    personaName: z.string().min(1).max(40).optional(),
    personaDescription: z.string().min(1).max(200).optional(),
    wikiEnabled: z.boolean().optional(),
    userModelSelectionEnabled: z.boolean().optional(),
    idleTimeoutMinutes: z.number().int().min(0).max(1440).optional(),
    dashboardDefaultDirection: DashboardDirectionSchema.optional(),
    dashboardDefaultIntent: DashboardIntentSchema.optional(),
    identityGuardEnabled: z.boolean().optional(),
    identityGuardPatterns: z.string().max(8000).optional(),
    identityCannedAnswer: z.string().max(2000).optional(),
    bannedWordsEnforce: z.boolean().optional(),
    bannedWordsRefusalText: z.string().max(2000).optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: '하나 이상의 필드를 지정해야 합니다.',
});
export const AuditLogQuerySchema = z.object({
    action: z.string().optional(),
    actorEmail: z.string().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    /** meta->>'status' 필터. 도구 호출 / artifact RPC 실패만 추리고 싶을 때 'error' 사용. */
    status: z.enum(['ok', 'error']).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
/**
 * 저장된 SQL 쿼리 감사 목록. LLM 이 실행하거나 저장한 모든 SQL 은 saved_queries 에만
 * 보관되며, 일반 사용자 화면에는 queryId (UUID) 만 노출된다. 관리자 전용.
 */
export const SavedQueryKindSchema = z.enum(['db', 'excel']);
export const SavedQueryListItemSchema = z.object({
    id: z.string().uuid(),
    ownerUserId: z.string().uuid(),
    ownerEmail: z.string().nullable(),
    ownerDisplayName: z.string().nullable(),
    sessionId: z.string().uuid().nullable(),
    sessionTitle: z.string().nullable(),
    /**
     * 쿼리가 생성된 대화 세션 row 가 존재하는지 (sessionId 가 null 이 아니고 join 으로 row 잡힘).
     * false 면 sessionId 자체가 NULL — orphan 정리 (delete-orphans) 대상.
     * 주의: soft-deleted 세션은 row 가 살아있으므로 sessionExists=true. 추가 표시는
     *       sessionDeletedAt 로 판단 (어드민은 보존 + 시각화, 자동 정리 대상 아님).
     */
    sessionExists: z.boolean(),
    /**
     * 세션이 soft-deleted 인 경우 그 시각. NULL = (활성 세션 OR sessionId 자체가 NULL).
     * UI 에서 "삭제됨 대화" 배지 (회색/amber) 표시용. orphan 카운트에 포함되지 않는다 —
     * saved_queries 는 감사 / 휴지통 의도로 보존 유지.
     */
    sessionDeletedAt: z.string().nullable(),
    kind: SavedQueryKindSchema,
    dataSourceId: z.string().uuid().nullable(),
    dataSourceName: z.string().nullable(),
    fileId: z.string().uuid().nullable(),
    fileName: z.string().nullable(),
    sql: z.string(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
    useCount: z.number().int(),
    /**
     * 가장 최근 실행 시 권한 룰 자동 주입 (data_source_table_scope_rules) 으로 인해
     * 실제 외부 DB 에 전달된 SQL. saved_queries.sql 은 LLM 원본을 보존하므로,
     * UI 가 "실제 돌아간 쿼리" 를 표시할 때 이 필드를 사용. 룰 적용 안 됐거나
     * 실행 audit 가 없으면 null.
     */
    lastExecutedSql: z.string().nullable().optional(),
    /** 실행 시 주입된 scope predicate 들 — 자동 주입 내역 배지 표시용. */
    lastScopeInjected: z
        .array(z.object({
        schemaName: z.string().nullable(),
        tableName: z.string(),
        column: z.string(),
        value: z.string(),
    }))
        .optional(),
});
export const SavedQueryListSchema = z.object({
    queries: z.array(SavedQueryListItemSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int(),
});
export const SavedQueryListQuerySchema = z.object({
    ownerEmail: z.string().optional(),
    kind: SavedQueryKindSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
/**
 * 단건 삭제 요청.
 * confirmActive=true 는 "살아있는 세션에 연결되어 있다는 걸 알면서도 삭제하겠다" 는 의미.
 * 서버는 orphan 이면 이 플래그 없이도 삭제하지만, 살아있는 세션이면 409 로 거부한다.
 */
export const SavedQueryDeleteRequestSchema = z.object({
    confirmActive: z.boolean().optional(),
});
export const SavedQueryDeleteResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true) }),
    z.object({
        ok: z.literal(false),
        reason: z.literal('active_session'),
        sessionId: z.string().uuid().nullable(),
        sessionTitle: z.string().nullable(),
    }),
]);
/**
 * 고아 쿼리 일괄 삭제. mode='filtered' 면 목록 필터 동일 조건으로 좁혀 삭제,
 * mode='all' 이면 조건 무시하고 전체 orphan 을 삭제. 한 번에 1000 건 상한.
 */
export const SavedQueryDeleteOrphansRequestSchema = z.object({
    mode: z.enum(['filtered', 'all']).default('filtered'),
    ownerEmail: z.string().optional(),
    kind: SavedQueryKindSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
export const SavedQueryDeleteOrphansResponseSchema = z.object({
    deleted: z.number().int(),
    /** 일괄 처리 상한에 걸려 일부만 제거된 경우 true. 재호출로 연속 처리 가능. */
    capped: z.boolean(),
});
/**
 * 관리자 전용 — 타인의 채팅 세션 본문 열람. 저장된 쿼리 감사 페이지에서만 진입한다.
 * 서버는 호출마다 audit_logs 에 `admin.chat_session.read` 를 남긴다.
 */
export const AdminChatSessionDetailSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    ownerUserId: z.string().uuid(),
    ownerEmail: z.string().nullable(),
    ownerDisplayName: z.string().nullable(),
    skillId: z.string().uuid().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** NULL = 활성 세션, NOT NULL = 사용자가 그 시각에 삭제 (soft delete). 어드민은 그대로 노출. */
    deletedAt: z.string().nullable(),
    messages: z.array(ChatMessageSchema),
});
// ─── 채팅 로그 어드민 ────────────────────────────────────────────────────────
export const AdminChatInterruptSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    userId: z.string().uuid(),
    kind: z.enum(['skill_select', 'ask_user', 'batch_ask_user']),
    prompt: InterruptPromptSchema,
    status: z.enum(['pending', 'resolved', 'canceled']),
    resolution: InterruptResolveRequestSchema.nullable(),
    createdAt: z.string(),
    resolvedAt: z.string().nullable(),
});
export const AdminChatArtifactSummarySchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(['html', 'react', 'svg', 'mermaid', 'markdown', 'code']),
    title: z.string(),
    currentVersion: z.number().int(),
    createdAt: z.string(),
});
export const AdminChatSessionSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    ownerUserId: z.string().uuid(),
    ownerEmail: z.string().nullable(),
    ownerDisplayName: z.string().nullable(),
    skillId: z.string().uuid().nullable(),
    skillName: z.string().nullable(),
    /** 세션에서 동작한 스킬 이름들. 일반 세션은 skillIds/skillId, 에이전트 세션은 agent.skillIds 에서 해석. */
    skillNames: z.array(z.string()),
    llmEndpointId: z.string().uuid().nullable(),
    llmEndpointName: z.string().nullable(),
    model: z.string().nullable(),
    extendedThinking: z.boolean(),
    agentId: z.string().uuid().nullable(),
    /** agentId 가 있을 때 해당 에이전트 이름 (없으면 null). */
    agentName: z.string().nullable(),
    /** 세션을 만든 클라이언트 — 'web' | 'desktop' (inniework). UI 에서 desktop 배지 표시. */
    clientOrigin: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** NULL = 활성, NOT NULL = 사용자가 삭제한 시각. UI 에서 회색 + 배지 처리. */
    deletedAt: z.string().nullable(),
    turnCount: z.number().int(),
    errorCount: z.number().int(),
    hasHitl: z.boolean(),
    /** 세션 내 메시지 중 금칙어가 탐지된 것이 하나라도 있는가. */
    hasBannedWord: z.boolean(),
});
export const AdminChatSessionListSchema = z.object({
    sessions: z.array(AdminChatSessionSummarySchema),
    total: z.number().int(),
    capped: z.boolean(),
});
export const AdminChatSessionListQuerySchema = z.object({
    userIds: z
        .preprocess((v) => (Array.isArray(v) ? v : v ? [v] : undefined), z.array(z.string().uuid()))
        .optional(),
    skillIds: z
        .preprocess((v) => (Array.isArray(v) ? v : v ? [v] : undefined), z.array(z.string().uuid()))
        .optional(),
    llmEndpointIds: z
        .preprocess((v) => (Array.isArray(v) ? v : v ? [v] : undefined), z.array(z.string().uuid()))
        .optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    hasError: z.enum(['true', 'false']).optional(),
    hasHitl: z.enum(['true', 'false']).optional(),
    agentOnly: z.enum(['true', 'false']).optional(),
    /** 'true' 면 inniework 데스크톱에서 만든 세션만. */
    desktopOnly: z.enum(['true', 'false']).optional(),
    /** 'true' 면 금칙어가 탐지된 메시지를 가진 세션만. */
    hasBannedWord: z.enum(['true', 'false']).optional(),
    /** 자유 검색 — 세션 title 또는 id 부분일치. 토글 없이 두 필드를 OR 로 매칭. */
    search: z.string().max(200).optional(),
});
/**
 * 챗 turn 실행 중 발생한 오류 — audit_logs (action='chat.turn_error') 의 meta 가
 * 그대로 노출된다. 관리자 화면이 사고 분석용으로 copy-paste 해서 모델과 같이 보기 위함.
 *
 * 모든 필드는 nullable/optional — meta jsonb 가 향후 진화해도 zod parse 가 깨지지 않게.
 */
export const AdminChatTurnErrorSchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string(),
    phase: z.enum(['llm_stream', 'run', 'attachment_ingest']).nullable(),
    errorName: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
    errorStack: z.string().nullable().optional(),
    sessionId: z.string().nullable().optional(),
    userId: z.string().nullable().optional(),
    userEmail: z.string().nullable().optional(),
    skillIds: z.array(z.string()).nullable().optional(),
    llmEndpointId: z.string().nullable().optional(),
    llmBondingId: z.string().nullable().optional(),
    model: z.string().nullable().optional(),
    provider: z.string().nullable().optional(),
    baseUrl: z.string().nullable().optional(),
    iteration: z.number().nullable().optional(),
    lastToolKey: z.string().nullable().optional(),
    pendingCallsCount: z.number().nullable().optional(),
    firstUserMessageId: z.string().nullable().optional(),
    firstUserMessageHead: z.string().nullable().optional(),
    turnStartedAt: z.string().nullable().optional(),
    errorAt: z.string().nullable().optional(),
});
export const AdminChatSessionTraceSchema = AdminChatSessionDetailSchema.extend({
    llmEndpointId: z.string().uuid().nullable(),
    llmEndpointName: z.string().nullable(),
    model: z.string().nullable(),
    skillNames: z.array(z.string()),
    extendedThinking: z.boolean(),
    agentId: z.string().uuid().nullable(),
    /** agentId 가 있을 때 해당 에이전트 이름 (없으면 null). */
    agentName: z.string().nullable(),
    /** 세션을 만든 클라이언트 — 'web' | 'desktop' (inniework). */
    clientOrigin: z.string(),
    interrupts: z.array(AdminChatInterruptSchema),
    artifacts: z.array(AdminChatArtifactSummarySchema),
    turnErrors: z.array(AdminChatTurnErrorSchema),
    turnCount: z.number().int(),
    errorCount: z.number().int(),
    hasHitl: z.boolean(),
});
/**
 * 첨부파일 감사 — 관리자 전용. 업로더 · 첨부된 대화 · 엑셀 인제스트 상태 · 연관 쿼리 수 포함.
 * sessionCount 가 0 이면 '고아' (어떤 대화도 이 파일을 참조하지 않음) — 안전하게 일괄 삭제 대상.
 */
export const AdminFileUploadListItemSchema = z.object({
    id: z.string().uuid(),
    ownerUserId: z.string().uuid(),
    ownerEmail: z.string().nullable(),
    ownerDisplayName: z.string().nullable(),
    originalName: z.string(),
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    kind: FileUploadKindSchema,
    status: FileUploadStatusSchema,
    error: z.string().nullable(),
    createdAt: z.string(),
    readyAt: z.string().nullable(),
    /** MinIO PUT 단계 (multipart + storage) 소요 ms. 옛 행은 null. */
    uploadMs: z.number().int().nonnegative().nullable(),
    /** 이 파일을 attachedFileIds 에 가진 chat_messages 가 속한 distinct 세션 수. 0 이면 orphan. */
    sessionCount: z.number().int().nonnegative(),
    /** 첨부된 세션 중 최근 일부 (최대 3개) 의 제목 + id — 상세에서 표시용. */
    sessionSamples: z.array(z.object({ id: z.string().uuid(), title: z.string().nullable() })),
    /** kind='excel' 일 때 이 파일을 참조하는 saved_queries 수. kind='other' 면 0. */
    linkedQueryCount: z.number().int().nonnegative(),
});
export const AdminFileUploadListSchema = z.object({
    files: z.array(AdminFileUploadListItemSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int(),
});
export const AdminFileUploadListQuerySchema = z.object({
    ownerEmail: z.string().optional(),
    kind: FileUploadKindSchema.optional(),
    /** 단일 status 필터 (예: 'processing'). */
    status: FileUploadStatusSchema.optional(),
    /** true 면 진행 중(uploading/queued/processing)만 — status 보다 우선. */
    activeOnly: z.coerce.boolean().optional(),
    /** mime prefix substring 매칭 (예: 'image/', 'application/pdf'). */
    mime: z.string().optional(),
    /** 'orphan' 이면 sessionCount=0 만, 'active' 면 sessionCount>0 만, 미지정은 전체. */
    orphan: z.enum(['orphan', 'active']).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
/** 진행 중 작업 현황 요약 — 관리자 모니터링 바. */
export const AdminFileUploadActiveSummarySchema = z.object({
    uploading: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    processing: z.number().int().nonnegative(),
});
/** 관리자 중지 요청 — 사용자에게 노출될 사유 문구. */
export const AdminFileUploadCancelRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
/** 채팅 세션 단위로 그룹화한 첨부 관리 뷰. */
export const AdminAttachmentSessionGroupSchema = z.object({
    sessionId: z.string().uuid().nullable(),
    sessionTitle: z.string().nullable(),
    ownerUserId: z.string().uuid().nullable(),
    ownerEmail: z.string().nullable(),
    ownerDisplayName: z.string().nullable(),
    files: z.array(AdminFileUploadListItemSchema),
});
export const AdminAttachmentBySessionSchema = z.object({
    groups: z.array(AdminAttachmentSessionGroupSchema),
    nextCursor: z.string().nullable(),
});
export const AdminAttachmentBySessionQuerySchema = z.object({
    ownerEmail: z.string().optional(),
    sessionId: z.string().uuid().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(5).max(50).default(20),
});
/** 문서 처리 설정 (system_config) — 관리자 첨부파일 설정 hub. */
export const DocumentProcessingConfigSchema = z.object({
    /** 사용자 문서 분류/해석 LLM 엔드포인트. null = 시스템 default 사용(없으면 LLM 단계 skip). */
    documentLlmEndpointId: z.string().uuid().nullable(),
    /** 사용자당 동시 변환 상한. */
    documentIngestMaxPerUser: z.number().int().min(1).max(20),
    /** 분류/해석 system 프롬프트. null/빈 문자열 = 내장 기본값. */
    documentAnalyzePrompt: z.string().nullable(),
    /** 사용자당 동시 이미지 OCR 처리 상한. */
    imageIngestMaxPerUser: z.number().int().min(1).max(20),
    /** 이미지 OCR system 프롬프트. null/빈 문자열 = 내장 기본값. */
    imageOcrPrompt: z.string().nullable(),
    /** 전역 비전 기본 모델이 설정돼 있는지 (read-only). false 면 이미지 첨부 비활성. */
    visionDefaultConfigured: z.boolean(),
});
/** 문서 처리 설정 업데이트 요청 (모든 필드 선택). */
export const DocumentProcessingConfigUpdateSchema = z.object({
    documentLlmEndpointId: z.string().uuid().nullable().optional(),
    documentIngestMaxPerUser: z.number().int().min(1).max(20).optional(),
    documentAnalyzePrompt: z.string().nullable().optional(),
    imageIngestMaxPerUser: z.number().int().min(1).max(20).optional(),
    imageOcrPrompt: z.string().nullable().optional(),
});
export const AdminFileUploadDeleteRequestSchema = z.object({
    confirmActive: z.boolean().optional(),
});
export const AdminFileUploadDeleteResponseSchema = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true) }),
    z.object({
        ok: z.literal(false),
        reason: z.literal('active_sessions'),
        sessionCount: z.number().int(),
        linkedQueryCount: z.number().int(),
    }),
]);
export const AdminFileUploadDeleteOrphansRequestSchema = z.object({
    ownerEmail: z.string().optional(),
    kind: FileUploadKindSchema.optional(),
    mime: z.string().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
export const AdminFileUploadDeleteOrphansResponseSchema = z.object({
    deleted: z.number().int(),
    capped: z.boolean(),
});
/**
 * 저장된 쿼리 관리자 preview — 1000건 상한 · 60초 statement_timeout · user_scope bypass.
 * useCount / lastUsedAt 는 증가하지 않음 (preview 는 관리자 감사이지 실제 사용이 아님).
 */
export const SavedQueryPreviewResponseSchema = z.object({
    queryId: z.string().uuid(),
    kind: z.enum(['db', 'excel']),
    columns: z.array(z.string()),
    rows: z.array(z.array(z.unknown())),
    rowCount: z.number().int(),
    truncated: z.boolean(),
    /** 컬럼 수가 100개 상한을 넘어 처음 100개만 반환된 경우 true. admin 라우트에서는 항상 false. */
    truncatedColumns: z.boolean(),
    injectedLimit: z.boolean(),
    latencyMs: z.number().int(),
});
// ─── Insight Agent 외부 로그 조회 ────────────────────────────────────────────
//
// analysis.verify_tag 가 호출한 후 에이전트 측에 쌓인 LLM 호출 로그를 어드민이 조회.
// 본 채팅 시스템과 별개로 운영되는 에이전트의 내부 로그를 그대로 노출 — 디버깅 / 비용 / 단계별
// 흐름 파악 목적. 자세한 통합 정책은 docs/19-external-agent-logs.md 참고.
/**
 * 알려진 error_type 값 — 에이전트가 응답에 마킹하는 한국어 짧은 코드.
 * 정의되지 않은 값이 오면 클라이언트 측 derive 가 '알 수 없음' 으로 폴백.
 */
export const KNOWN_AGENT_ERROR_TYPES = [
    '서버에러',
    '미학습',
    '데이터없음',
    '미완료',
    '유효성미달',
    '범위 외 질문',
];
/**
 * 한 LLM 호출의 로그 entry. cacheTimestamp not null = 캐시 적용됨.
 * status 필드 없음 — error_type + cacheTimestamp 조합으로 클라이언트가 derive.
 *
 * 시간 필드는 agent 가 "YYYY-MM-DD HH:mm:ss" (offset 없음) 또는 ISO 8601 with offset 으로 보낼 수
 * 있어 `z.string()` 로 느슨하게 받음. 서버 프록시 (`normalizeAgentLogEntry`) 가 클라이언트가
 * `new Date()` 로 안전히 파싱할 수 있는 형식 (`T` 구분자 + Z 또는 offset) 으로 정규화.
 */
export const InsightAgentLogEntrySchema = z.object({
    llmName: z.string(),
    inputValue: z.string().nullable(),
    prompt: z.string().nullable(),
    answer: z.string().nullable(),
    modelName: z.string(),
    inputToken: z.number().int(),
    outputToken: z.number().int(),
    startTimestamp: z.string(),
    endTimestamp: z.string(),
    errorType: z.string().nullable(),
    errorMsg: z.string().nullable(),
    cacheTimestamp: z.string().nullable(),
});
/** 응답은 LLM 호출 array. 빈 배열이면 "로그 없음". */
export const AdminInsightAgentLogResponseSchema = z.array(InsightAgentLogEntrySchema);
// ─── 사용 통계 (admin) ─────────────────────────────────────────────────────────
/**
 * 사용 통계 공통 필터. 기간 + multi-select user/skill/llm.
 *
 * Fastify query parser 는 단일 값을 string, 다중을 array 로 줘서 schema 가 단일 값
 * 거부. preprocess 로 string → [string] 강제 변환.
 */
const arrayQuerySchema = (item) => z.preprocess((v) => (Array.isArray(v) ? v : v ? [v] : undefined), z.array(item)).optional();
export const AdminUsageStatsQuerySchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    userIds: arrayQuerySchema(z.string().uuid()),
    skillIds: arrayQuerySchema(z.string().uuid()),
    llmEndpointIds: arrayQuerySchema(z.string().uuid()),
});
/** 일자별 시계열 단건. 차트 탭별로 다른 series 를 사용. */
export const AdminUsageStatsDailySchema = z.object({
    date: z.string(), // YYYY-MM-DD (KST)
    /** DAU — 이 날짜에 턴을 보낸 distinct user. */
    userCount: z.number().int(),
    /** 이 날짜 auth.login* 이벤트 (필터 매칭 사용자 한정). */
    visitCount: z.number().int(),
    sessions: z.number().int(),
    turns: z.number().int(),
    /** tool_calls JSONB error 턴 수. */
    toolErrors: z.number().int(),
    /** audit_logs chat.turn_error 수. */
    systemErrors: z.number().int(),
    /** assistant 메시지 input_tokens 합. */
    inputTokens: z.number().int(),
    /** assistant 메시지 output_tokens 합. */
    outputTokens: z.number().int(),
});
/** 사용자 ranking 단건. */
export const AdminUsageStatsTopUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    turnCount: z.number().int(),
    /** 보조 표기용 — 사용자가 만든 distinct 세션 수. */
    sessionCount: z.number().int(),
});
/** 스킬 ranking 단건. skillId null = 스킬 미지정 일반 chat. turn 기준 정렬. */
export const AdminUsageStatsTopSkillSchema = z.object({
    skillId: z.string().uuid().nullable(),
    name: z.string(),
    slug: z.string().nullable(),
    /** 주 정렬값 — 이 스킬로 처리된 turn 수. */
    turnCount: z.number().int(),
    /** 보조 표기용 — distinct 세션 수. */
    sessionCount: z.number().int(),
});
/** 전체 top-N 페이지네이션 쿼리. offset/limit 기반. */
export const AdminUsageStatsTopListQuerySchema = AdminUsageStatsQuerySchema.extend({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(10).max(100).default(50),
});
export const AdminUsageStatsTopUsersListSchema = z.object({
    users: z.array(AdminUsageStatsTopUserSchema),
    total: z.number().int(),
});
export const AdminUsageStatsTopSkillsListSchema = z.object({
    skills: z.array(AdminUsageStatsTopSkillSchema),
    total: z.number().int(),
});
/** Top 도구 — tool_calls JSONB 에서 추출. callCount desc 정렬, errorRate 보조. */
export const AdminUsageStatsTopToolSchema = z.object({
    name: z.string(),
    callCount: z.number().int(),
    errorCount: z.number().int(),
    /** 0.0~1.0 비율. UI 에서 % 변환. */
    errorRate: z.number().min(0).max(1),
});
export const AdminUsageStatsTopToolsListSchema = z.object({
    tools: z.array(AdminUsageStatsTopToolSchema),
    total: z.number().int(),
});
/** Overview 응답 — KPI + 일자별 추이 + 사용자/스킬 top10. */
export const AdminUsageStatsOverviewSchema = z.object({
    userCount: z.number().int(),
    /** audit_logs 의 auth.login* 이벤트 수 (필터 매칭 사용자에 한해). */
    visitCount: z.number().int(),
    sessionCount: z.number().int(),
    turnCount: z.number().int(),
    errorCount: z.number().int(),
    /** tool_calls JSONB 안 status='error' 인 턴 수 (도구 호출 실패). */
    toolErrorCount: z.number().int(),
    /** audit_logs action='chat.turn_error' (LLM 스트림 / chat run / 첨부 ingest 실패). */
    systemErrorCount: z.number().int(),
    inputTokenSum: z.number().int(),
    inputTokenAvgPerTurn: z.number(),
    outputTokenSum: z.number().int(),
    outputTokenAvgPerTurn: z.number(),
    responseTimeP50Ms: z.number().int().nullable(),
    responseTimeP95Ms: z.number().int().nullable(),
    daily: z.array(AdminUsageStatsDailySchema),
    topUsers: z.array(AdminUsageStatsTopUserSchema),
    topSkills: z.array(AdminUsageStatsTopSkillSchema),
    topTools: z.array(AdminUsageStatsTopToolSchema),
});
//# sourceMappingURL=admin.js.map